// Visual primitives — Skeleton, ErrorBanner, EmptyState, Pagination,
// Spinner. Shared across pages.

import type { ReactNode } from 'react';

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span
      className="spinner"
      role="status"
      aria-label={label}
      aria-busy="true"
    />
  );
}

export function Skeleton({
  width,
  height = '1em',
  rows = 1,
  ariaLabel,
}: {
  width?: string;
  height?: string;
  rows?: number;
  ariaLabel?: string;
}) {
  if (rows <= 1) {
    return (
      <span
        className="skeleton"
        style={{ width, height, display: 'inline-block' }}
        aria-label={ariaLabel}
      />
    );
  }
  const items: ReactNode[] = [];
  for (let i = 0; i < rows; i += 1) {
    items.push(
      <span
        key={i}
        className="skeleton skeleton-row"
        style={{ width, height }}
      />,
    );
  }
  return <div aria-label={ariaLabel}>{items}</div>;
}

export function ErrorBanner({
  message,
  requestId,
  onRetry,
  retryLabel = 'Try again',
}: {
  message: string;
  requestId?: string;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  return (
    <div className="error-banner" role="alert">
      <div className="error-banner-message">{message}</div>
      {requestId ? (
        <div className="error-banner-ref">Reference: {requestId}</div>
      ) : null}
      {onRetry ? (
        <button type="button" className="btn btn-secondary" onClick={onRetry}>
          {retryLabel}
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  message,
  action,
}: {
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <p>{message}</p>
      {action}
    </div>
  );
}

export function Pagination({
  nextCursor,
  loading,
  onNext,
}: {
  nextCursor: string | null;
  loading: boolean;
  onNext: () => void;
}) {
  if (!nextCursor) return null;
  return (
    <div className="pagination">
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onNext}
        disabled={loading}
        aria-busy={loading ? 'true' : 'false'}
        aria-label="Show more results"
      >
        {loading ? 'Loading…' : 'Show more'}
      </button>
    </div>
  );
}

export function StatusBadge({
  status,
}: {
  status: 'draft' | 'active' | 'completed' | 'archived';
}) {
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span
      className={`badge badge-status badge-status-${status}`}
      aria-label={`Status: ${label}`}
    >
      {label}
    </span>
  );
}

export function DerivedStatusBadge({
  derivedStatus,
}: {
  derivedStatus: 'active' | 'upcoming' | 'past';
}) {
  const label =
    derivedStatus.charAt(0).toUpperCase() + derivedStatus.slice(1);
  return (
    <span
      className={`badge badge-derived badge-derived-${derivedStatus}`}
      aria-label={`Derived status: ${label}`}
    >
      {label}
    </span>
  );
}
